import { z } from 'zod';
import {
  CATEGORIES,
  MARKET_SORTS,
  MARKET_STATUSES,
  MAX_FEE_BPS,
  MAX_OUTCOMES,
  MIN_OUTCOMES,
} from '../constants';
import {
  httpUrlSchema,
  isoDateSchema,
  localizedDraftSchema,
  paginationQuerySchema,
  santimSchema,
  text,
} from './common';

export const outcomeKeySchema = z
  .string()
  .regex(/^[a-z0-9_]{1,32}$/, 'Outcome key: 1-32 chars of a-z, 0-9, _');

export const outcomeInputSchema = z.object({
  key: outcomeKeySchema,
  title: localizedDraftSchema(120),
});
export type OutcomeInput = z.infer<typeof outcomeInputSchema>;

const marketFields = {
  title: localizedDraftSchema(200),
  description: localizedDraftSchema(2000),
  category: z.enum(CATEGORIES),
  closesAt: isoDateSchema,
  resolutionDate: isoDateSchema,
  resolutionSource: z.object({
    en: z.string().trim().max(200),
    am: z.string().trim().max(200),
    url: httpUrlSchema.optional(),
  }),
  outcomes: z.array(outcomeInputSchema).min(MIN_OUTCOMES).max(MAX_OUTCOMES),
  featured: z.boolean(),
  imageUrl: httpUrlSchema.optional(),
  feeBps: z.number().int().min(0).max(MAX_FEE_BPS),
  minStake: santimSchema,
  maxStake: santimSchema,
};

interface MarketRefineInput {
  closesAt?: string;
  resolutionDate?: string;
  outcomes?: { key: string }[];
  minStake?: number;
  maxStake?: number;
}

function refineMarket(value: MarketRefineInput, ctx: z.RefinementCtx): void {
  if (
    value.closesAt !== undefined &&
    value.resolutionDate !== undefined &&
    Date.parse(value.resolutionDate) < Date.parse(value.closesAt)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['resolutionDate'],
      message: 'resolutionDate must not be before closesAt',
    });
  }
  if (value.outcomes) {
    const keys = value.outcomes.map((o) => o.key);
    if (new Set(keys).size !== keys.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['outcomes'],
        message: 'Outcome keys must be unique',
      });
    }
  }
  if (
    value.minStake !== undefined &&
    value.maxStake !== undefined &&
    value.minStake > value.maxStake
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['minStake'],
      message: 'minStake must not exceed maxStake',
    });
  }
}

/** Admin: create a draft market. feeBps/minStake/maxStake default from Config when omitted. */
export const createMarketSchema = z
  .object({
    ...marketFields,
    featured: marketFields.featured.default(false),
    feeBps: marketFields.feeBps.optional(),
    minStake: marketFields.minStake.optional(),
    maxStake: marketFields.maxStake.optional(),
  })
  .superRefine(refineMarket);
export type CreateMarketBody = z.infer<typeof createMarketSchema>;

/** Admin: update a market. Which fields are allowed depends on status (enforced in the service). */
export const updateMarketSchema = z
  .object(marketFields)
  .partial()
  .superRefine(refineMarket)
  .refine((v) => Object.keys(v).length > 0, 'Provide at least one field');
export type UpdateMarketBody = z.infer<typeof updateMarketSchema>;

export interface PublishableMarket {
  title: { en: string; am: string };
  resolutionSource: { en: string; am: string };
  outcomes: { key: string; title: { en: string; am: string } }[];
  closesAt: string | Date;
  resolutionDate: string | Date;
}

/**
 * Returns the list of problems that block publishing a draft (empty list = publishable).
 * Publishing requires both languages for title, resolution source and outcomes, >= 2 outcomes,
 * and a closing time in the future.
 */
export function listPublishProblems(market: PublishableMarket, now: Date = new Date()): string[] {
  const problems: string[] = [];
  const filled = (v: string) => v.trim().length > 0;
  if (!filled(market.title.en) || !filled(market.title.am)) {
    problems.push('title must be provided in English and Amharic');
  }
  if (!filled(market.resolutionSource.en) || !filled(market.resolutionSource.am)) {
    problems.push('resolutionSource must be provided in English and Amharic');
  }
  if (market.outcomes.length < MIN_OUTCOMES) {
    problems.push(`at least ${MIN_OUTCOMES} outcomes are required`);
  }
  if (market.outcomes.some((o) => !filled(o.title.en) || !filled(o.title.am))) {
    problems.push('every outcome title must be provided in English and Amharic');
  }
  const closes = new Date(market.closesAt).getTime();
  const resolves = new Date(market.resolutionDate).getTime();
  if (!(closes > now.getTime())) problems.push('closesAt must be in the future');
  if (resolves < closes) problems.push('resolutionDate must not be before closesAt');
  return problems;
}

/** Feed cursors are opaque strings: they encode the sort keys, not just an id. */
export const opaqueCursorSchema = z.string().max(300).optional();

export const listMarketsQuerySchema = paginationQuerySchema.extend({
  cursor: opaqueCursorSchema,
  category: z.enum(CATEGORIES).optional(),
  q: text(1, 100).optional(),
  sort: z.enum(MARKET_SORTS).default('trending'),
  status: z.enum(['open', 'locked', 'resolved']).default('open'),
});
export type ListMarketsQuery = z.infer<typeof listMarketsQuerySchema>;

export const adminListMarketsQuerySchema = paginationQuerySchema.extend({
  status: z.enum(MARKET_STATUSES).optional(),
  category: z.enum(CATEGORIES).optional(),
  q: text(1, 100).optional(),
});
export type AdminListMarketsQuery = z.infer<typeof adminListMarketsQuerySchema>;

export const proposeResolutionSchema = z.object({
  outcomeKey: outcomeKeySchema,
  evidenceNote: text(3, 2000),
  evidenceUrl: httpUrlSchema.optional(),
});
export type ProposeResolutionBody = z.infer<typeof proposeResolutionSchema>;
